//#region types
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
import { CurrencyEnums, DimensionEnums } from "models/enums";
import { ReceiptDraftRuleSectionFormViewProps } from "models/propTypes/automationRulePropTypes";
import { SelectInputProps, SearchInputProps, NumberInputProps, ToggleProps } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  DimensionChipSelector,
  FormFieldLayout,
  GroupSection,
  HorizontalFormField,
  NumberInput,
  SearchInput,
  SelectInput,
  Suggestions,
  Toggle,
  VerticalFormField,
} from "components/common/wintForms";
import MinMaxAmountSection from "components/automationRule/forms/common/minMaxAmountSection";
import { useReceiptDraftSuppliers } from "components/automationRule/forms/receiptDraft";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";
import useCreateReceiptDraftFormHelpers from "./useCreateFormHelper";
import { IconDefinitionConstants } from "utils/constants";

const ReceiptDraftCreateSection: React.FC<ReceiptDraftRuleSectionFormViewProps> = (props) => {
  const { connect, isTodo, isEditView, receiptAmount, currency } = props;
  const { receiptCategories, wintCardPaymentMenthods, receiptCategorySuggestions } = useCreateReceiptDraftFormHelpers();
  const { topWintCardSuppliers, searchSupplier } = useReceiptDraftSuppliers();

  const suggestions: SelectionItem[] =
    receiptCategorySuggestions ?? receiptCategories.slice(0, 3).map((item) => item as SelectionItem) ?? [];
  const categoryProps = connect("receiptDraft.category");

  const onCategorySuggestionSelect = (category: SelectionItem) => {
    categoryProps.setValue(category, true);
  };

  return (
    <>
      <GroupSection header="" className={cssExports.noTitle}>
        <VerticalFormField<SelectInputProps>
          Component={SelectInput}
          {...connect("receiptDraft.paymentMethodId")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AutomationRule.WintCard)}
          items={wintCardPaymentMenthods}
          icon={IconDefinitionConstants.WIncome2}
          disabled={isTodo}
        />
        <MinMaxAmountSection
          minAmount={connect("receiptDraft.minimumAmount")}
          maxAmount={connect("receiptDraft.maximumAmount")}
        />
        <VerticalFormField<NumberInputProps>
          Component={NumberInput}
          {...connect("receiptDraft.maxAutomatedEventsPerMonth")}
          label={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.AutomationRule.MonthlyProcessedReceipts
          )}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.AutomationRule.MonthlyProcessedReceiptsDescription
          )}
          icon={IconDefinitionConstants.WMoney}
          optional
        />
        <VerticalFormField<SearchInputProps>
          Component={SearchInput}
          {...connect("receiptDraft.supplier")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.Supplier)}
          suggestedItems={topWintCardSuppliers}
          items={searchSupplier}
          icon={IconDefinitionConstants.WUser}
          disabled={isTodo}
        />
        <VerticalFormField<SearchInputProps>
          Component={SearchInput}
          {...categoryProps}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.RefersTo)}
          items={receiptCategories}
          icon={IconDefinitionConstants.WVat}
        />
        <Suggestions setValue={onCategorySuggestionSelect} suggestions={suggestions} />
        {isEditView && (
          <HorizontalFormField<ToggleProps>
            Component={Toggle}
            {...connect("receiptDraft.isActive")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.IsActive)}
          />
        )}
      </GroupSection>
      <GroupSection
        header={TranslationKeys.Common.Tags}
        description={TranslationKeys.AutomationRule.TagsHelpText}
        className={cssExports.noBorder}
      >
        <DimensionChipSelector
          {...connect("receiptDraft.dimensions")}
          type={DimensionEnums.DimensionCategory.Receipt}
          allowDefaultDimensions
          amount={receiptAmount ?? 0}
          currency={currency ?? CurrencyEnums.CurrencyCode.SEK}
          layout={<FormFieldLayout direction="vertical" />}
          enableSingleSelectionMode
          hideSelectionDialogFooter
          viewAllButtonText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ViewAll)}
        />
      </GroupSection>
    </>
  );
};

export default ReceiptDraftCreateSection;
